import _ from "lodash";
import { AxiosError } from "axios";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  QueryKey,
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
  UseQueryResult,
} from "@tanstack/react-query";
import { useSnackBar } from "@truevault/aquilo";
import { NetworkRequest, NetworkRequestState } from "../models/NetworkRequest";
import { useIsMounted } from "./useIsMounted";
import { isEditModeBlockedError } from "../../polaris/edit-mode/editModeState";

export type FetchFn = () => Promise<void>;
export type RefreshFn = () => Promise<void>;
export type UpdateFn = (a?: any) => Promise<void>;
export type InvalidateFn = () => Promise<void> | void;

type ApiMessages = {
  defaultError?: string;
  forceError?: string;
  success?: string;
};

export type ApiArgs<R> = {
  api: (v?: any) => Promise<R>;
  messages?: ApiMessages;
  allowConcurrent?: boolean;
  dependencies?: React.DependencyList;
  notReady?: boolean;
  notifyError?: boolean;
  notifySuccess?: boolean;
  onSuccess?: (result: R, request: NetworkRequestState, args?: any) => void;
  onError?: (request: NetworkRequestState, error: Error | AxiosError) => void;
};

export type ApiData<R> = {
  result: R | undefined;
  request: NetworkRequestState;
  refresh: RefreshFn;
  invalidate: InvalidateFn;
  queryKey: QueryKey;
  isSettled?: boolean; // true if the data is from an API fetch result and not from cache
};

export type DataRequestArgs<R> = {
  queryKey: QueryKey;
  notReady?: boolean;
  staleTime?: number;
  cacheTime?: number;
  invalidation?: InvalidationOptions;
  keepPreviousData?: boolean;
  /** react-query's retry option; defaults to its 3 retries with exponential backoff. */
  retry?: boolean | number;
  /** react-query's retryDelay in ms; defaults to its exponential backoff starting at 1s. */
  retryDelay?: number;
} & ApiArgs<R>;

export type InvalidationOptions = _.DebounceSettings & {
  wait?: number;
};

export const useDataRequest = <R>(args: DataRequestArgs<R>): ApiData<R> => {
  const {
    queryKey,
    api,
    messages,
    notReady,
    notifySuccess = false,
    notifyError = true,
    keepPreviousData = true,
    invalidation,
  } = args;

  const queryClient = useQueryClient();
  const enqueueSnackbar = useSnackBar();
  const { networkRequestState, setRequestStatus } = useNetworkStateWithStatus(messages);
  const [isSettled, setIsSettled] = useState(false);

  const {
    data: result,
    refetch,
    status,
  } = useQuery<R, Error | AxiosError>(queryKey, api, {
    keepPreviousData,
    enabled: !notReady,
    retry: args.retry,
    retryDelay: args.retryDelay,
    staleTime: args.staleTime,
    cacheTime: args.cacheTime,
    onSettled: () => {
      setIsSettled(true);
    },
    onSuccess: (data) => {
      if (args.onSuccess) {
        args.onSuccess(data, networkRequestState);
      }

      if (networkRequestState.successMessage && notifySuccess) {
        enqueueSnackbar(networkRequestState.successMessage, { variant: "success" });
      }
    },
    onError: (err) => {
      if (isEditModeBlockedError(err)) {
        return;
      }

      console.error(err);

      if (args.onError) {
        args.onError(networkRequestState, err);
      }

      if (networkRequestState.errorMessage && notifyError) {
        enqueueSnackbar(networkRequestState.errorMessage, { variant: "error" });
      }
    },
  });

  useEffect(() => {
    setRequestStatus(status);
  }, [status, setRequestStatus]);

  // @ts-ignore
  const refresh: RefreshFn = useCallback(async () => {
    const result = await refetch();
    return result.data;
  }, [refetch]);

  const _invalidate = () => queryClient.invalidateQueries(queryKey);
  const invalidate = useCallback(
    invalidation?.wait ? _.debounce(_invalidate, invalidation.wait, invalidation) : _invalidate,
    [],
  );

  // Derive the request state synchronously from the query status to avoid the
  // useEffect lag. Without this, there is a 1-2 render window where useQuery
  // reports status="loading" but networkRequestState still shows unstarted
  // (started=false, running=false), because the status is synced through two
  // chained useEffects. During that gap, callers checking
  // NetworkRequest.anyRunning() would incorrectly see no request in flight.
  const request = useMemo((): NetworkRequestState => {
    if (status === "loading" && !networkRequestState.running) {
      return NetworkRequest.start();
    }
    return networkRequestState;
  }, [status, networkRequestState]);

  return { queryKey, result, request, refresh, invalidate, isSettled };
};

export const useDataRequests = <T>(
  queries: { queryKey: unknown[]; queryFn: (v?: any) => Promise<T> }[],
): [UseQueryResult<T>[], NetworkRequestState, () => Promise<void>] => {
  const { networkRequestState, setNetworkRequestState, setRequestStatus } =
    useNetworkStateWithStatus();

  const results = useQueries({
    queries,
  });

  useEffect(() => {
    if (results.every((q) => q.status === "success")) {
      setRequestStatus("success");
    } else if (results.some((q) => q.status === "error")) {
      setRequestStatus("error");
    } else if (results.some((q) => q.status === "loading")) {
      setRequestStatus("loading");
    } else {
      setNetworkRequestState(NetworkRequest.unstarted());
    }
  }, [results, setNetworkRequestState, setRequestStatus]);

  const refreshAll = useCallback(async () => {
    await Promise.all(results.map((q) => q.refetch()));
  }, [results]);

  return [results, networkRequestState, refreshAll];
};

type ApiAction<R, P extends any = any> = {
  fetch: (a?: P) => Promise<void>;
  fetchWithResult: (a?: P) => Promise<R>;
  result: R | undefined;
  request: NetworkRequestState;
  resetState: () => void;
};

/**
 * @deprecated We should no longer use action requests. Use react-query mutators instead.
 */
export const useActionRequest = <R, P extends any = any>(args: ApiArgs<R>): ApiAction<R, P> => {
  const { api, messages, notReady, notifySuccess = false, notifyError = true } = args;
  const enqueueSnackbar = useSnackBar();
  const { networkRequestState, setNetworkRequestState, setRequestStatus } =
    useNetworkStateWithStatus(messages);
  const isMounted = useIsMounted();

  const {
    data: result,
    mutateAsync,
    status,
    reset,
  } = useMutation(api, {
    onSuccess: (data) => {
      if (args.onSuccess) {
        args.onSuccess(data, networkRequestState);
      }

      const successMessage = networkRequestState.successMessage || messages?.success;
      if (successMessage && notifySuccess) {
        enqueueSnackbar(successMessage, { variant: "success" });
      }

      reset();
      if (isMounted()) {
        setNetworkRequestState((prev) => NetworkRequest.success(prev, messages?.success));
      }
    },
    onError: (err) => {
      if (isEditModeBlockedError(err)) {
        reset();
        if (isMounted()) {
          setNetworkRequestState(NetworkRequest.unstarted());
        }
        return;
      }

      console.error(err);

      if (args.onError) {
        args.onError(networkRequestState, err as Error | AxiosError);
      }

      const errorMessage =
        networkRequestState.errorMessage ??
        messages?.defaultError ??
        "An unknown error occurred while processing this request";

      if (errorMessage && notifyError) {
        enqueueSnackbar(errorMessage, { variant: "error" });
      }

      reset();
      if (isMounted()) {
        setNetworkRequestState((prev) => NetworkRequest.failed(prev, errorMessage));
      }
    },
  });

  // this variation of fetch returns Promise<void> and is suitable for fire-and-forget updates
  const fetchCb = useCallback(
    async (a?: any) => {
      if (!notReady) {
        await mutateAsync(a);
      }
    },
    [mutateAsync, notReady],
  );

  // this variation of fetch returns Promise<R>, and is suitable for immediate usage of the response data
  const fetchWithResultCb = useCallback(
    async (a: P | undefined) => {
      if (!notReady) {
        return await mutateAsync(a);
      }
      throw new Error("Cannot fetch because the request is not yet ready.");
    },
    [mutateAsync, notReady],
  );

  useEffect(() => {
    setRequestStatus(status);
  }, [status, setRequestStatus]);

  const resetState = useCallback(() => {
    setNetworkRequestState(NetworkRequest.unstarted());
  }, [setNetworkRequestState]);

  return {
    fetch: fetchCb,
    fetchWithResult: fetchWithResultCb,
    result,
    request: networkRequestState,
    resetState,
  };
};

/**
 * @deprecated The method should not be used
 */
export const useApiTrigger = <T, R>(
  api: (values: T) => Promise<any>,
  done: (response?: R) => void,
  errorCopy: string,
  successCopy?: string,
): UseApiTriggerResult => {
  const { fetch: start, request } = useActionRequest<R>({
    api: api,
    messages: { defaultError: errorCopy, success: successCopy },
    notifySuccess: true,
    notifyError: true,
    onSuccess: done,
  });

  return [start, request];
};

type NetworkRequestStatus = "error" | "success" | "loading" | "idle" | "unstarted";

export const useNetworkStateWithStatus = (messages?: ApiMessages) => {
  const [status, setRequestStatus] = useState<NetworkRequestStatus>("unstarted");
  const [networkRequestState, setNetworkRequestState] = useState<NetworkRequestState>(
    NetworkRequest.unstarted(),
  );

  useEffect(() => {
    if (status === "error") {
      setNetworkRequestState((prev) =>
        NetworkRequest.failed(prev, messages?.defaultError ?? "Unknown Error"),
      );
    } else if (status === "success") {
      setNetworkRequestState((prev) => NetworkRequest.success(prev, messages?.success));
    } else if (status === "idle") {
      setNetworkRequestState((prev) => {
        if (prev.running) {
          return NetworkRequest.unstarted();
        }

        return prev;
      });
    } else if (status === "loading") {
      setNetworkRequestState(NetworkRequest.start());
    }
  }, [status, messages?.defaultError, messages?.success]);

  return { networkRequestState, setNetworkRequestState, setRequestStatus };
};

type UseApiTriggerResult = [
  (v: any) => Promise<void>, // trigger
  NetworkRequestState,
];

export type UseApiData<R> = {
  data: R;
  request: NetworkRequestState;
  refresh: RefreshFn;
  invalidate: InvalidateFn;

  remote: R | undefined;
};
